import { ConvexError } from 'convex/values'
/**
 * Calendar-day handling. Read this before touching anything date-shaped.
 *
 * A habit completion belongs to a *calendar day*, not an instant. "Did I
 * meditate today?" has a different answer in Berlin and in Auckland at the
 * same moment, so a day is only meaningful paired with a timezone.
 *
 * Rules:
 *  - Instants are UTC epoch milliseconds.
 *  - Calendar days are `YYYY-MM-DD` strings, branded as CalendarDate.
 *  - Converting instant -> day requires an explicit IANA timezone. There is
 *    deliberately no function that reads the ambient device timezone; the
 *    user's stored zone is the only authority, so the answer doesn't change
 *    when they open the app on a plane.
 */

declare const calendarDateBrand: unique symbol

export type CalendarDate = string & { readonly [calendarDateBrand]: true }

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export function isCalendarDate(value: string): value is CalendarDate {
  const match = CALENDAR_DATE.exec(value)
  if (match === null) return false
  // Reject impossible dates like 2026-02-30, which the regex alone allows.
  return toUtcNoon(value as CalendarDate) !== null
}

export function asCalendarDate(value: string): CalendarDate {
  if (!isCalendarDate(value)) {
    throw new ConvexError(`Not a calendar date: ${JSON.stringify(value)}`)
  }
  return value
}

/** Midday UTC sidesteps DST entirely for pure calendar arithmetic. */
function toUtcNoon(date: CalendarDate): Date | null {
  const match = CALENDAR_DATE.exec(date)
  if (match === null) return null
  const [, year, month, day] = match
  const parsed = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), 12),
  )
  // Date.UTC rolls overflow forward, so round-trip to catch invalid input.
  if (parsed.getUTCMonth() !== Number(month) - 1) return null
  if (parsed.getUTCDate() !== Number(day)) return null
  return parsed
}

function fromUtcNoon(date: Date): CalendarDate {
  return date.toISOString().slice(0, 10) as CalendarDate
}

/**
 * Wall-clock parts of an instant, in the given zone.
 *
 * `Intl.DateTimeFormat` rather than a wrapper date object, because a wrapper
 * that cannot resolve the zone falls back to the *host's* offset — which is
 * indistinguishable from working right up until the machine's zone differs
 * from the user's, and then silently off by an hour at every day boundary.
 * Intl either knows the zone or throws.
 */
function wallClock(instant: number, timeZone: string): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(instant))

  const found: Record<string, string> = {}
  for (const part of parts) found[part.type] = part.value
  return found
}

/** The calendar day an instant falls on, in the given zone. */
export function calendarDateOf(
  instant: number,
  timeZone: string,
): CalendarDate {
  const parts = wallClock(instant, timeZone)
  return `${parts['year']}-${parts['month']}-${parts['day']}` as CalendarDate
}

/**
 * How far into the day an instant is, in minutes, in the given zone.
 *
 * Reminders are set as a time of day, so this is the other half of comparing
 * "now" with "eight in the morning" without either side knowing about zones.
 */
export function minutesIntoDay(instant: number, timeZone: string): number {
  const parts = wallClock(instant, timeZone)
  return Number(parts['hour']) * 60 + Number(parts['minute'])
}

/**
 * How late a day is allowed to run before it becomes the next one.
 *
 * Midnight is a clock's answer, not a person's. A night routine finished at
 * half past midnight belongs to the evening it was part of, and filing it
 * under the following morning is what turns "I did it every day" into a
 * broken streak.
 *
 * Capped well short of noon because past that it stops being a late night and
 * starts being a different definition of a day.
 */
export const MAX_DAY_START_MINUTES = 8 * 60

export function isValidDayStart(minutes: number): boolean {
  return (
    Number.isInteger(minutes) &&
    minutes >= 0 &&
    minutes <= MAX_DAY_START_MINUTES
  )
}

/**
 * Today, as the user's timezone *and* day boundary define it.
 *
 * Implemented by shifting the instant rather than by comparing the wall-clock
 * hour, which matters twice a year: during the autumn repeat 01:30 happens
 * twice and a wall-clock comparison cannot tell the two apart, while shifting
 * the instant lands both in the day that was still running.
 */
/**
 * Which calendar day an instant belongs to.
 *
 * Not the same question as which day it falls on by the clock: with the day
 * set to roll over at 3am, something recorded at 00:30 belongs to the evening
 * that has not finished yet. Shifting the instant rather than the date is what
 * keeps that correct across a DST boundary.
 *
 * `todayIn` is this question asked about now. Anything asking it about a
 * stored `completedAt` wants this one.
 */
export function calendarDayOf(
  instant: number,
  timeZone: string,
  dayStartsAtMinutes = 0,
): CalendarDate {
  return calendarDateOf(instant - dayStartsAtMinutes * 60_000, timeZone)
}

export function todayIn(
  timeZone: string,
  now: number,
  dayStartsAtMinutes = 0,
): CalendarDate {
  return calendarDayOf(now, timeZone, dayStartsAtMinutes)
}

/**
 * Whether the clock has passed midnight but the day has not yet rolled over.
 *
 * The one thing a shifted boundary has to say out loud: at 1am the app shows
 * yesterday's date, and without a word of explanation that reads as a bug
 * rather than as the feature you asked for.
 */
export function isBeforeDayStart(
  timeZone: string,
  now: number,
  dayStartsAtMinutes = 0,
): boolean {
  if (dayStartsAtMinutes <= 0) return false
  return minutesIntoDay(now, timeZone) < dayStartsAtMinutes
}

export function addCalendarDays(
  date: CalendarDate,
  days: number,
): CalendarDate {
  const noon = toUtcNoon(date)
  if (noon === null) throw new ConvexError(`Not a calendar date: ${date}`)
  noon.setUTCDate(noon.getUTCDate() + days)
  return fromUtcNoon(noon)
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from: CalendarDate, to: CalendarDate): number {
  const a = toUtcNoon(from)
  const b = toUtcNoon(to)
  if (a === null || b === null) throw new ConvexError('Not a calendar date')
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/** 0 = Sunday through 6 = Saturday. */
export function weekdayOf(date: CalendarDate): number {
  const noon = toUtcNoon(date)
  if (noon === null) throw new ConvexError(`Not a calendar date: ${date}`)
  return noon.getUTCDay()
}

/** First day of the week containing `date`, given the user's week start. */
export function startOfWeek(
  date: CalendarDate,
  weekStartsOn: number,
): CalendarDate {
  const offset = (weekdayOf(date) - weekStartsOn + 7) % 7
  return addCalendarDays(date, -offset)
}

/** Calendar parts, for month arithmetic that day-offsets cannot express. */
export function partsOf(date: CalendarDate): {
  year: number
  month: number
  day: number
} {
  const noon = toUtcNoon(date)
  if (noon === null) throw new ConvexError(`Not a calendar date: ${date}`)
  return {
    year: noon.getUTCFullYear(),
    month: noon.getUTCMonth() + 1,
    day: noon.getUTCDate(),
  }
}

/** Days in the given month. `month` is 1-based. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0, 12)).getUTCDate()
}

/**
 * Build a calendar date, clamping the day to the end of the month.
 *
 * "The 31st of every month" has to mean something in February. Clamping to the
 * 28th/29th keeps the schedule monthly; skipping the month instead would make
 * a monthly chore silently quarterly.
 */
export function calendarDateFromParts(
  year: number,
  month: number,
  day: number,
): CalendarDate {
  const clamped = Math.min(day, daysInMonth(year, month))
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${year}-${pad(month)}-${pad(clamped)}` as CalendarDate
}

/** Same day-of-month `months` later, clamped to the target month's length. */
export function addCalendarMonths(
  date: CalendarDate,
  months: number,
  preferredDay?: number,
): CalendarDate {
  const { year, month, day } = partsOf(date)
  const zeroBased = year * 12 + (month - 1) + months
  return calendarDateFromParts(
    Math.floor(zeroBased / 12),
    (zeroBased % 12) + 1,
    preferredDay ?? day,
  )
}
